import { useEffect, useRef, useState } from "react";
import ReactHlsPlayer from "react-hls-player";
import { BlockButton } from "../Button/BlockButton";
import { IconButton } from "../Button/IconButton";

interface VideoPlayerModalProps {
	isOpen: boolean;
	onClose: () => void;
	videoUrl: string;
	title?: string;
	poster?: string;
	sharelink?: string;
}

const VideoPlayerModal = ({
	isOpen,
	onClose,
	videoUrl,
	title,
	poster,
	sharelink,
}: VideoPlayerModalProps) => {
	const playerRef = useRef<HTMLVideoElement>(
		null,
	) as React.RefObject<HTMLVideoElement>;
	const [isVideoReady, setIsVideoReady] = useState(false);

	// Reset video ready state when modal closes or video URL changes
	useEffect(() => {
		if (!isOpen) {
			setIsVideoReady(false);
		}
	}, [isOpen]);

	// Handle Escape key to close modal
	useEffect(() => {
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				onClose();
			}
		};

		if (isOpen) {
			document.addEventListener("keydown", handleEscape);
			document.body.style.overflow = "hidden";
		}

		return () => {
			document.removeEventListener("keydown", handleEscape);
			document.body.style.overflow = "";
		};
	}, [isOpen, onClose]);

	const handlePlaying = () => {
		// onPlaying fires when playback starts after being paused or delayed
		// This is more reliable for HLS streams which need time to buffer
		if (!isVideoReady) {
			setIsVideoReady(true);
		}
	};

	if (!isOpen) return null;

	// Determine if URL is HLS stream
	const isHlsStream =
		videoUrl.includes(".m3u8") || videoUrl.includes("playback");

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
			onClick={onClose}
		>
			<div
				className="relative w-full max-w-4xl mx-4"
				onClick={(e) => e.stopPropagation()}
			>
				{/* Close button */}
				<div className="absolute -top-14 left-0 right-0 z-10 flex justify-between items-center">
					{/* Title */}
					{title && (
						<p className="text-white text-lg font-medium truncate max-w-[calc(100%-60px)]">
							{title}
						</p>
					)}
					<IconButton
						type="TertiaryWhite"
						icon="cross_outline_black"
						size="Large"
						onClick={onClose}
						disabled={false}
						loading={false}
						loadingIcon=""
					/>
				</div>

				{/* Video Player */}
				<div className="relative aspect-video bg-black rounded-lg overflow-hidden">
					{/* Loading spinner */}
					{!isVideoReady && (
						<div className="absolute inset-0 flex items-center justify-center z-10">
							<div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin" />
						</div>
					)}

					{/* Video element - hidden until ready */}
					<div
						className="absolute inset-0"
						style={{ visibility: isVideoReady ? "visible" : "hidden" }}
					>
						{isHlsStream ? (
							<ReactHlsPlayer
								playerRef={playerRef}
								src={videoUrl}
								autoPlay={true}
								muted={true}
								controls={true}
								width="100%"
								height="100%"
								poster={poster}
								hlsConfig={{
									maxLoadingDelay: 4,
									minAutoBitrate: 0,
									lowLatencyMode: true,
								}}
								onPlaying={handlePlaying}
							/>
						) : (
							<video
								ref={playerRef}
								src={videoUrl}
								autoPlay
								muted
								controls
								poster={poster}
								className="w-full h-full object-contain"
								onPlaying={handlePlaying}
							/>
						)}
					</div>
				</div>
				{sharelink && (
					<div className="w-fit mx-auto mt-5">
						<BlockButton
							type="Primary"
							width="296px"
							icon="arrow_square_outline"
							size="Large"
							disabled={false}
							loading={false}
							loadingIcon=""
							onClick={() => {
								window.open(sharelink, "_blank");
							}}
						>
							Edit Highlight in App
						</BlockButton>
					</div>
				)}
			</div>
		</div>
	);
};

export { VideoPlayerModal };
